import { useEffect, useRef, useState } from "react";
import type { PdfJsDocumentView } from "../presentation/cognitoryPdfJsGovernedPageLoader";
import { isGovernedPdfRenderCancellation } from "../presentation/governedPdfCanvasRenderLifecycle";
import {
  MAIN_PAGE_COLORS,
  MAIN_PAGE_FONT_STACK,
} from "../theme/mainPageVisual";

const THUMBNAIL_TARGET_WIDTH_PX = 84;
const THUMBNAIL_RENDER_SCALE = 0.2;

function resolveCanvasOutputScale(): number {
  if (typeof window === "undefined") {
    return 1;
  }
  return window.devicePixelRatio > 1 ? window.devicePixelRatio : 1;
}

export function GovernedCitationPdfThumbnail(input: {
  pdf: PdfJsDocumentView;
  pageNumber: number;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );

  useEffect(() => {
    let cancelled = false;
    let activeRenderTask: { cancel: () => void } | null = null;

    void (async () => {
      try {
        const page = await input.pdf.getPage(input.pageNumber);
        if (cancelled) {
          return;
        }

        const viewport = page.getViewport({ scale: THUMBNAIL_RENDER_SCALE });
        const canvas = canvasRef.current;
        if (!canvas) {
          return;
        }

        const outputScale = resolveCanvasOutputScale();
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${THUMBNAIL_TARGET_WIDTH_PX}px`;
        canvas.style.height = "auto";

        const context = canvas.getContext("2d");
        if (!context) {
          throw new Error("Unable to acquire thumbnail canvas context.");
        }

        context.setTransform(outputScale, 0, 0, outputScale, 0, 0);
        const renderTask = page.render({
          canvasContext: context,
          viewport,
        });
        activeRenderTask = renderTask;
        await renderTask.promise;

        if (!cancelled) {
          setStatus("ready");
        }
      } catch (error) {
        if (cancelled || isGovernedPdfRenderCancellation(error)) {
          return;
        }
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
      activeRenderTask?.cancel();
    };
  }, [input.pageNumber, input.pdf]);

  return (
    <div
      data-testid={`cognitory-governed-pdf-thumbnail-canvas-wrap-${input.pageNumber}`}
      style={{
        width: THUMBNAIL_TARGET_WIDTH_PX,
        margin: "0 auto",
        minHeight: 108,
        display: "grid",
        placeItems: "center",
        background: MAIN_PAGE_COLORS.pageBackground,
        borderRadius: 4,
        overflow: "hidden",
      }}
    >
      <canvas
        ref={canvasRef}
        data-testid={`cognitory-governed-pdf-thumbnail-canvas-${input.pageNumber}`}
        style={{
          display: status === "ready" ? "block" : "none",
        }}
      />
      {status === "loading" ? (
        <span
          style={{
            fontSize: "0.68rem",
            color: MAIN_PAGE_COLORS.muted,
            fontFamily: MAIN_PAGE_FONT_STACK,
          }}
        >
          …
        </span>
      ) : null}
      {status === "error" ? (
        <span
          style={{
            fontSize: "0.68rem",
            color: MAIN_PAGE_COLORS.alertInk,
            fontFamily: MAIN_PAGE_FONT_STACK,
          }}
        >
          —
        </span>
      ) : null}
    </div>
  );
}
